import React, { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../teacher/AuthContext';
import { BookOpen, Users, ShieldCheck } from 'lucide-react';

const RoleSelection = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { currentUser, login } = useAuth();

  // 1. Intercept Social Auth Tokens on Page Initialization
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const token = params.get('token');
    const userData = params.get('user');

    if (token && userData) {
      try {
        // Parse information back out into standard JSON objects
        const parsedUser = JSON.parse(decodeURIComponent(userData));
        
        // Seed AuthContext variables so the app knows we are safely verified
        login(token, parsedUser);
        
        // Wipe query strings from browser bar to clean user view space
        navigate('/roleselection', { replace: true });
      } catch (err) {
        console.error("Failed to parse social login user payload data:", err);
      }
    }
  }, [location, login, navigate]);

  const roleConfig = {
    admin: '/tutoring/admin-dashboard',
    tutor: '/tutoring/tutors_23',
    learner: '/tutoring/tutors_23'
  };

  const handleRoleChoice = (selectedRole) => {
    // Update the AuthContext with the chosen role
    const updatedUser = { ...currentUser, activeRole: selectedRole };
    
    // We reuse the login function to persist this choice in state
    const token = localStorage.getItem('token');
    login(token, updatedUser);

    // Redirect based on choice
    navigate(roleConfig[selectedRole]);
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center p-6">
      <div className="text-center mb-10">
        <h1 className="text-3xl font-bold text-gray-900">
          Welcome, {currentUser?.email || 'User'}
        </h1>
        <p className="text-gray-600 mt-2">How would you like to proceed today?</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-5xl w-full">
        {/* Tutor Option */}
        <button 
          onClick={() => handleRoleChoice('tutor')}
          className="bg-white p-8 rounded-2xl shadow-lg border-2 border-transparent hover:border-purple-500 transition-all group text-left"
        >
          <div className="bg-purple-100 p-4 rounded-xl w-fit mb-4 group-hover:bg-purple-600 group-hover:text-white transition-colors">
            <Users size={32} />
          </div>
          <h2 className="text-xl font-bold text-gray-800">I am a Tutor</h2>
          <p className="text-gray-500 mt-2">Manage your availability, view bookings, and help students learn.</p>
        </button>

        {/* Learner Option */}
        <button 
          onClick={() => handleRoleChoice('learner')}
          className="bg-white p-8 rounded-2xl shadow-lg border-2 border-transparent hover:border-blue-500 transition-all group text-left"
        >
          <div className="bg-blue-100 p-4 rounded-xl w-fit mb-4 group-hover:bg-blue-600 group-hover:text-white transition-colors">
            <BookOpen size={32} />
          </div>
          <h2 className="text-xl font-bold text-gray-800">I am a Learner</h2>
          <p className="text-gray-500 mt-2">Find experts, book sessions, and grow your skills.</p>
        </button>

        {/* Admin Option - Only show if they actually have admin rights in DB */}
        {currentUser?.role === 'admin' && (
          <button 
            onClick={() => handleRoleChoice('admin')}
            className="bg-white p-8 rounded-2xl shadow-lg border-2 border-transparent hover:border-red-500 transition-all group text-left"
          >
            <div className="bg-red-100 p-4 rounded-xl w-fit mb-4 group-hover:bg-red-600 group-hover:text-white transition-colors">
              <ShieldCheck size={32} />
            </div>
            <h2 className="text-xl font-bold text-gray-800">Administrator</h2>
            <p className="text-gray-500 mt-2">Approve tutors, manage payments, and site settings.</p>
          </button>
        )}
      </div>
    </div>
  );
};

export default RoleSelection;